import { useCallback, useEffect, useMemo, useState } from 'react';

import { isRefusedError, readRefusal } from '@/api/api';
import { managerAssessmentService } from '@/api/cycle-evaluation-services/manager-assessment';
import { useAmelioTranslations } from '@/hooks';
import { useAiResult } from '@/hooks/ai/useAiResult';
import { captureError } from '@/plugins/Sentry';
import { type ContentLanguage } from '@/utils/contentLanguage';
import { getLocalizedText, type LocalizedText } from '@/utils/localization';

const GENERATION_ERROR_FALLBACK = 'Failed to generate summary.';

/** The `code` the server refuses with when a summary has to be generated and the plan has no Performance PRO. */
const PRO_REQUIRED_CODE = 'PerformanceProRequired';

/**
 * Why the summary isn't showing. `assessmentNotReady` is a legitimate, already-friendly state (the
 * assessment simply hasn't been submitted yet). `planLocked` is the other expected one: nothing is
 * saved yet and generating a summary needs Performance PRO, which the company's plan does not have.
 * `generationFailed` covers everything else — the AI gateway timing out, returning a malformed
 * payload, failing its own response validation, etc. Only the reason is exposed to the UI; the
 * technical detail behind it is logged for the team, never shown to a manager or HR user (#15591).
 */
export type SummaryErrorReason = 'assessmentNotReady' | 'planLocked' | 'generationFailed';

function getApiErrorCode(error: unknown): string | undefined {
    const response = (error as { response?: { data?: { errorCode?: string } } } | null)?.response;
    return response?.data?.errorCode;
}

export interface SummarySection {
    title: string;
    description: string;
}

export interface ManagerAssessmentSummary {
    /**
     * The one language the AI text (the overview and the descriptions) is written in, the reader's when it was
     * generated; null when unknown. Competency names (titles) are bilingual and read in the reader's language.
     */
    language: ContentLanguage | null;
    overview: string;
    strengths: SummarySection[];
    developmentAreas: SummarySection[];
}

const languageOf = (language: unknown): ContentLanguage | null =>
    language === 'en' || language === 'fr' ? language : null;

const pickSections = (
    items: { title?: LocalizedText; description?: string }[] | undefined,
): SummarySection[] =>
    (items ?? []).map((item) => ({
        // The competency's bilingual name, read in the reader's language; the AI text is shown as written.
        title: getLocalizedText(item.title),
        description: item.description ?? '',
    }));

// The backend validates the shape and delivers clean JSON ({ language, summary, strengths, developmentAreas }: the
// summary and descriptions plain strings, the titles { en, fr } names); the try/catch is only a last-resort guard
// against a genuinely broken payload.
function parseSummary(content: string): ManagerAssessmentSummary {
    try {
        const parsed = JSON.parse(content);
        return {
            language: languageOf(parsed.language),
            overview: parsed.summary ?? '',
            strengths: pickSections(parsed.strengths),
            developmentAreas: pickSections(parsed.developmentAreas),
        };
    } catch {
        // Not JSON — surface the raw text rather than dropping it.
        return { language: null, overview: content, strengths: [], developmentAreas: [] };
    }
}

/**
 * Generates the manager-assessment summary and receives the result over SignalR (same pattern as the
 * insights hook, single result). The frontend owns the correlationId; the backend serves an already
 * generated summary for the cycle from cache, so a repeat call returns instantly. The summary is written in one
 * language, the reader's (Accept-Language) when it was generated, and is shown as is to a reader of the other one.
 */
export function useManagerAssessmentSummary(cycleInstanceId: string | undefined) {
    const { i18n } = useAmelioTranslations();

    const [correlationId, setCorrelationId] = useState<string | null>(null);
    const [error, setError] = useState<SummaryErrorReason | null>(null);
    // A saved summary is returned inline in the POST response (no SignalR); a fresh one arrives over SignalR.
    const [cachedContent, setCachedContent] = useState<string | null>(null);

    const result = useAiResult(correlationId);

    // Changing cycle drops the previous request/result.
    useEffect(() => {
        setCorrelationId(null);
        setError(null);
        setCachedContent(null);
    }, [cycleInstanceId]);

    useEffect(() => {
        if (result && !result.success) {
            captureError({
                error: new Error(result.error ?? GENERATION_ERROR_FALLBACK),
                context: { tags: { area: 'manager-assessment-summary', action: 'generate' } },
            });
            setError('generationFailed');
        }
    }, [result]);

    const content = cachedContent ?? (result?.success ? (result.content ?? null) : null);
    // getLocalizedText (the titles) reads i18n.language internally; depend on it so a language switch re-picks them.
    const summary = useMemo(
        () => (content ? parseSummary(content) : null),
        [content, i18n.language],
    );

    const generate = useCallback(async () => {
        if (!cycleInstanceId) return;
        const id = crypto.randomUUID();
        setError(null);
        setCachedContent(null);
        setCorrelationId(id); // arm the SignalR subscription before a fresh result could arrive
        try {
            const response = await managerAssessmentService.generateSummary({
                cycleInstanceId,
                correlationId: id,
            });
            if (response.status === 'cached' && response.content) {
                // Served straight from the DB — use it now; there is no SignalR result to wait for.
                setCachedContent(response.content);
                setCorrelationId(null);
            } else if (response.correlationId && response.correlationId !== id) {
                // A generation for this cycle was already in flight (e.g. after a page refresh) — attach to
                // it by subscribing to its correlationId instead of waiting on the one we just sent (which
                // was not dispatched to the gateway).
                setCorrelationId(response.correlationId);
            }
        } catch (cause) {
            if (getApiErrorCode(cause) === 'ASSESSMENT_NOT_READY') {
                setError('assessmentNotReady');
                return;
            }

            if (readRefusal(cause)?.code === PRO_REQUIRED_CODE) {
                setError('planLocked');
                return;
            }

            // A refusal is expected behaviour, not a fault: the user is told, the team is not paged.
            if (isRefusedError(cause)) {
                setError('generationFailed');
                return;
            }

            captureError({
                error: cause instanceof Error ? cause : new Error(GENERATION_ERROR_FALLBACK),
                context: { tags: { area: 'manager-assessment-summary', action: 'generate' } },
            });
            setError('generationFailed');
        }
    }, [cycleInstanceId]);

    // In flight once fired, until the result (SignalR or inline) or an error arrives.
    const isGenerating =
        correlationId !== null && result === undefined && error === null && cachedContent === null;

    return { summary, generate, isGenerating, error };
}
